import {
  Component,
  inject,
  OnInit,
  ViewChild,
  signal,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  ToastContainerComponent,
  LoadingIndicatorComponent,
  SidebarComponent,
  SidebarConfigService,
  BreadcrumbsComponent,
  AssetsCategory,
  ButtonComponent,
  IconComponent,
  LoaderService,
} from '@invessio/ui';
import { SIDEBAR_MENU_CONFIG } from './globalConfig/sidebar-menu.config';
import { PermissionService } from './globalServices/permission.service';
import { PermissionPolicy } from './globalServices/permission.policy.service';
import { Subscription } from 'rxjs';

@Component({
  imports: [
    RouterModule,
    LoadingIndicatorComponent,
    ToastContainerComponent,
    SidebarComponent,
    BreadcrumbsComponent,
    CommonModule,
    ButtonComponent,
    IconComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'ignyte';
  userDisplayName = '';
  permissionService = inject(PermissionService);
  permissionPolicy = inject(PermissionPolicy);
  sidebarConfigService = inject(SidebarConfigService);
  showSidebarAndHeader = true;
  showBreadcrumbs = true;
  showErrorFooter = false;
  isAuthenticated = false;
  readonly isOperationsAdmin = this.permissionPolicy.isOperationsAdmin;
  readonly isAdmin = this.permissionPolicy.isAdmin;
  isLoading = false;
  /** True when on /students/applications/:id - used to hide sidebar/toggle on mobile */
  isApplicationDetailPage = false;
  @ViewChild(SidebarComponent) sidebar?: SidebarComponent;
  AssetsCategory = AssetsCategory;
  sidebarExpanded = signal(true);
  sidebarAriaLabel = computed(() =>
    this.sidebarExpanded() ? 'Collapse sidebar' : 'Expand sidebar'
  );
  isLoggingOut = signal(false);
  private readonly spinnerExcludedRoutes = ['/offer', '/auth'];

  private permissionCheckSubscription?: Subscription;

  constructor(private router: Router, public loaderService: LoaderService) {
    // Listen to route changes to hide/show sidebar and header
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url: string = event.urlAfterRedirects ?? event.url ?? '';
        const routeData = this.getRouteData(this.router.routerState.snapshot.root);
        this.showSidebarAndHeader =
          (!routeData.hideSidebarAndHeader &&
          !url.includes('/offer-details') &&
          !url.includes('/offer') &&
          !url.includes('/students/coborrower') &&
          !url.includes('/auth')) ||
        ((url.includes('/offer-details') || url.includes('/offer')) &&
        this.isAuthenticated
        );
        this.showBreadcrumbs =
          !routeData.hideSidebarAndHeader &&
          !url.includes('/analytics-dashboard');
        this.showErrorFooter = !!routeData.hideSidebarAndHeader;
        this.isApplicationDetailPage = /\/students\/applications\/[^/]+/.test(
          url
        );
        if (url.includes('/auth')) {
          this.isLoggingOut.set(false);
        }
      });

    effect(() => {
      this.updateAuthState();
    });
  }

  ngOnDestroy(): void {
    if (this.permissionCheckSubscription) {
      this.permissionCheckSubscription.unsubscribe();
    }
  }

  private isSpinnerExcludedRoute(url: string): boolean {
    // hide spinner on certain routes like /offer and /auth where there are no guards
    return this.spinnerExcludedRoutes.some((route) => url.includes(route));
  }

  private getRouteData(route: ActivatedRouteSnapshot | null): any {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      if (current.data && current.data['hideSidebarAndHeader']) {
        return current.data;
      }
      current = current.firstChild;
    }
    return {};
  }

  ngOnInit(): void {
    const isAuthenticated = sessionStorage.getItem('isAuthenticated');

    if (isAuthenticated === 'true') {
      this.permissionService.setIsAuthenticated(true);
    }

    if (this.isSpinnerExcludedRoute(this.router.url)) {
      this.loaderService.show();
    }
    // Configure the sidebar with the app-specific menu configuration
    this.sidebarConfigService.setMenuConfiguration(SIDEBAR_MENU_CONFIG);

    const initialRouteData = this.getRouteData(this.router.routerState.snapshot.root);
    this.showSidebarAndHeader =
  !initialRouteData.hideSidebarAndHeader &&
      !this.router.url.includes('/offer-details') &&
      !this.router.url.includes('/offer') &&
      !this.router.url.includes('/students/coborrower') &&
      !this.router.url.includes('/auth');
    this.showBreadcrumbs =
      !initialRouteData.hideSidebarAndHeader &&
      !this.router.url.includes('/analytics-dashboard');
    this.showErrorFooter = !!initialRouteData.hideSidebarAndHeader;
    this.isApplicationDetailPage = /\/students\/applications\/[^/]+/.test(
      this.router.url
    );
    this.updateAuthState();
  }

  private updateAuthState(): void {
    this.isAuthenticated = this.permissionService.isAuthenticated();
    // Get user info from ApplicationConfiguration response for now.
    // We'll either add more metadata to that response or have a separate
    // endpoint to fetch user info after login.
    // sessionStorage.setItem('userInfo', active ? JSON.stringify(active) : '');
    this.permissionCheckSubscription = this.permissionService.permissionsLoaded$.subscribe(
      (loaded: boolean) => {
        if (loaded) {
          this.userDisplayName = this.permissionService.getDisplayName();
        }
      }
    );
  }

  toggleSidebar(): void {
    if (this.sidebar) {
      this.sidebar.toggleSidebar();
      this.sidebarExpanded.set(this.sidebar.isExpanded());
    }
  }

  public async signOut() {
    await this.permissionService.signOut();
  }

}
